import { useRef, useState } from "react";
import { reorder } from "./utils";

/**
 * useRowDrag — minimal HTML5 drag-and-drop for reordering an array of rows.
 *
 * @param list      The array being reordered.
 * @param onChange  Called with the reordered array.
 * @param scope     Optional prefix for the drag-handle data-testid. Defaults to "row".
 *                  Pass e.g. "materials", "labour", "sundries" so each table has its
 *                  own scoped handle ids: `materials-drag-handle-0`, etc.
 *
 * Returns:
 *   - dragProps(idx): props to spread on the <tr> element
 *   - handleProps(idx): props to spread on the drag handle (e.g. the GripVertical icon)
 *   - overIdx: index currently being hovered (for visual feedback)
 */
export function useRowDrag(list, onChange, scope = "row") {
  const [draggingIdx, setDraggingIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  // Mirror state in a ref so synchronous drag events don't fight the React render cycle.
  const draggingRef = useRef(null);

  const handleProps = (idx) => ({
    draggable: true,
    onDragStart: (e) => {
      draggingRef.current = idx;
      setDraggingIdx(idx);
      try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* ignore */ }
      e.dataTransfer.effectAllowed = "move";
    },
    onDragEnd: () => { draggingRef.current = null; setDraggingIdx(null); setOverIdx(null); },
    "data-testid": `${scope}-drag-handle-${idx}`,
    title: "Drag to reorder",
    style: { cursor: draggingIdx === idx ? "grabbing" : "grab" },
  });

  const dragProps = (idx) => ({
    onDragOver: (e) => {
      const from = draggingRef.current;
      if (from == null || from === idx) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      setOverIdx(idx);
    },
    onDrop: (e) => {
      e.preventDefault();
      const from = draggingRef.current;
      if (from != null && from !== idx) {
        onChange(reorder(list, from, idx));
      }
      draggingRef.current = null;
      setDraggingIdx(null);
      setOverIdx(null);
    },
    style: {
      opacity: draggingIdx === idx ? 0.4 : 1,
      borderTop: overIdx === idx && draggingIdx != null && draggingIdx !== idx ? "2px solid #2756C4" : undefined,
    },
  });

  return { dragProps, handleProps, draggingIdx, overIdx };
}
